import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner, Alert, Tabs, Tab, Badge, ButtonGroup } from 'react-bootstrap';
import apiClient from '../../../utils/axios';
import { format, parse } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './StatsPage.css';
import { groupName as resolveGroupName, groupSelectOptions } from '../../../config/groups';

const StatsPage = () => {
  useEffect(() => {
    document.title = '統計數據';
  }, []);

  // 狀態管理
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [activeTab, setActiveTab] = useState('monthly');
  const [selectedGroup, setSelectedGroup] = useState('all'); // 單選模式：選擇的團體
  const [selectedGeneration, setSelectedGeneration] = useState('all'); // 單選模式：選擇的期生
  const [advancedMode, setAdvancedMode] = useState(false); // 是否啟用進階篩選模式
  const [selectedFilters, setSelectedFilters] = useState(new Set()); // 進階模式：存儲選中的團體-期生組合

  // 排序狀態
  const [sortField, setSortField] = useState('total');
  const [sortDirection, setSortDirection] = useState('desc');

  // 日期選擇狀態
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = 2017; year <= currentYear; year++) {
    yearOptions.push(year);
  }

  // 月份選項
  const monthOptions = [
    { value: 1, label: '1月' },
    { value: 2, label: '2月' },
    { value: 3, label: '3月' },
    { value: 4, label: '4月' },
    { value: 5, label: '5月' },
    { value: 6, label: '6月' },
    { value: 7, label: '7月' },
    { value: 8, label: '8月' },
    { value: 9, label: '9月' },
    { value: 10, label: '10月' },
    { value: 11, label: '11月' },
    { value: 12, label: '12月' }
  ];

  // 團體選項（含「全部團體」）
  const groupOptions = groupSelectOptions(true);

  // 獲取月度統計數據
  const fetchMonthlyStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/stats/monthly?year=${selectedYear}&month=${selectedMonth}`);

      if (response.data) {
        setStatsData(response.data);
      }
    } catch (error) {
      setError('獲取統計數據時發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  // 獲取每日統計數據
  const fetchDailyStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/stats/daily?date=${selectedDate}`);

      if (response.data) {
        setStatsData(response.data);
      }
    } catch (error) {
      setError('獲取統計數據時發生錯誤，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // 根據活動標籤獲取對應統計數據
  const fetchStats = useCallback(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyStats();
    } else if (activeTab === 'daily') {
      fetchDailyStats();
    }
  }, [activeTab, fetchMonthlyStats, fetchDailyStats]);

  // 當標籤、年份、月份或日期變化時獲取新數據
  useEffect(() => {
    fetchStats();
  }, [fetchStats]); // 添加 fetchStats 作為依賴項

  // 處理表單提交
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStats();
  };

  // 處理年份變更
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  // 處理月份變更
  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  // 處理日期變更
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // 切換篩選模式
  const handleModeToggle = () => {
    setAdvancedMode(!advancedMode);
    // 切換模式時清除篩選
    if (!advancedMode) {
      setSelectedFilters(new Set());
    } else {
      setSelectedGroup('all');
      setSelectedGeneration('all');
    }
  };

  // 單選模式：處理團體選擇變更
  const handleGroupChange = (groupValue) => {
    setSelectedGroup(groupValue);
    setSelectedGeneration('all');
  };

  // 單選模式：處理期生選擇變更
  const handleGenerationChange = (generationValue) => {
    setSelectedGeneration(generationValue);
  };

  // 單選模式：獲取可用的期生選項
  const getAvailableGenerations = (stats) => {
    if (!stats || selectedGroup === 'all') return [];

    const groupData = stats[selectedGroup];
    if (!groupData) return [];

    const generations = Object.keys(groupData)
      .filter(gen => gen !== '0')
      .sort((a, b) => parseInt(a) - parseInt(b));

    return generations;
  };

  // 進階模式：處理篩選項的切換
  const handleFilterToggle = (group, generation) => {
    const filterKey = `${group}:${generation}`;
    const newFilters = new Set(selectedFilters);

    if (newFilters.has(filterKey)) {
      newFilters.delete(filterKey);
    } else {
      newFilters.add(filterKey);
    }

    setSelectedFilters(newFilters);

  };

  // 全選當前數據的所有期生
  const handleSelectAll = (stats) => {
    if (!stats) return;

    const newFilters = new Set();
    Object.entries(stats).forEach(([group, generations]) => {
      Object.keys(generations).forEach(gen => {
        if (gen !== '0') {
          newFilters.add(`${group}:${gen}`);
        }
      });
    });

    setSelectedFilters(newFilters);

  };

  // 進階模式：清除所有篩選
  const handleClearAll = () => {
    setSelectedFilters(new Set());
  };

  // 檢查某個組合是否被選中
  const isFilterSelected = (group, generation) => {
    return selectedFilters.has(`${group}:${generation}`);
  };

  // 獲取所有可用的團體和期生組合
  const getAvailableFilters = (stats) => {
    if (!stats) return {};

    const filters = {};
    Object.entries(stats).forEach(([group, generations]) => {
      filters[group] = Object.keys(generations)
        .filter(gen => gen !== '0')
        .sort((a, b) => parseInt(a) - parseInt(b));
    });

    return filters;
  };

  // 過濾選定團體的數據
  const getFilteredStats = (stats) => {
    if (!stats) return {};

    // 進階模式：使用多選篩選
    if (advancedMode) {
      // 如果沒有選中任何組合，顯示全部數據（移除 0 期生）
      if (selectedFilters.size === 0) {
        const filteredStats = {};
        Object.entries(stats).forEach(([group, generations]) => {
          const filteredGenerations = { ...generations };
          delete filteredGenerations['0'];
          if (Object.keys(filteredGenerations).length > 0) {
            filteredStats[group] = filteredGenerations;
          }
        });
        return filteredStats;
      }

      // 根據選中的篩選項過濾數據
      const filteredStats = {};
      selectedFilters.forEach(filterKey => {
        const [group, generation] = filterKey.split(':');
        if (stats[group] && stats[group][generation]) {
          if (!filteredStats[group]) {
            filteredStats[group] = {};
          }
          filteredStats[group][generation] = stats[group][generation];
        }
      });
      return filteredStats;
    }

    // 單選模式：原本的篩選邏輯
    let filteredStats = {};

    if (selectedGroup === 'all') {
      filteredStats = { ...stats };
    } else if (stats[selectedGroup]) {
      filteredStats[selectedGroup] = stats[selectedGroup];
    }

    // 從所有團體中移除 0 期生
    Object.keys(filteredStats).forEach(group => {
      const generations = { ...filteredStats[group] };
      delete generations['0'];
      filteredStats[group] = generations;
    });

    // 過濾期生
    if (selectedGeneration !== 'all' && selectedGroup !== 'all') {
      Object.keys(filteredStats).forEach(group => {
        const generations = filteredStats[group];
        if (generations[selectedGeneration]) {
          filteredStats[group] = {
            [selectedGeneration]: generations[selectedGeneration]
          };
        } else {
          filteredStats[group] = {};
        }
      });
    }

    return filteredStats;
  };

  // 轉換數據為圖表格式
  const prepareChartData = (stats) => {
    if (!stats) return [];

    // 獲取過濾後的數據
    const statsToUse = getFilteredStats(stats);
    const chartData = [];

    // 遍歷每個組
    Object.entries(statsToUse).forEach(([group, generations]) => {
      // 遍歷每個期別
      Object.entries(generations).forEach(([generation, members]) => {
        // 遍歷每個成員
        Object.entries(members).forEach(([memberName, messageTypes]) => {
          // 創建圖表數據項
          const dataItem = {
            name: memberName,
            group: group,
            generation: generation,
            total: messageTypes.total || 0,
            text: messageTypes[0] || 0,
            image: messageTypes[1] || 0,
            audio: messageTypes[3] || 0,
            video: messageTypes[4] || 0,
            kana: messageTypes.kana || null
          };

          chartData.push(dataItem);
        });
      });
    });

    // 按照訊息總數排序
    const sortedData = chartData.sort((a, b) => b.total - a.total);

    // 為每個項目添加固定的排名（基於訊息總數）
    return sortedData.map((item, index) => ({
      ...item,
      rank: index + 1
    }));
  };

  // 獲取組名的顯示文本（未知則回原始碼）
  const getGroupDisplayName = (groupCode) => resolveGroupName(groupCode) || groupCode;



  // 獲取每個組的成員數量
  const getMemberCountByGroup = (stats) => {
    if (!stats) return {};

    const filteredStats = getFilteredStats(stats);
    const counts = {};

    Object.entries(filteredStats).forEach(([group, generations]) => {
      let memberCount = 0;
      // 遍歷每個期別
      Object.entries(generations).forEach(([generation, members]) => {
        memberCount += Object.keys(members).length;
      });
      counts[group] = memberCount;
    });

    return counts;
  };

  // 獲取每個組的訊息總數
  const getMessageCountByGroup = (stats) => {
    if (!stats) return {};

    const filteredStats = getFilteredStats(stats);
    const counts = {};

    Object.entries(filteredStats).forEach(([group, generations]) => {
      let messageCount = 0;
      // 遍歷每個期別
      Object.entries(generations).forEach(([generation, members]) => {
        messageCount += Object.values(members).reduce((sum, messageTypes) => sum + (messageTypes.total || 0), 0);
      });
      counts[group] = messageCount;
    });

    return counts;
  };

  // 處理表格列排序
  const handleSort = (field) => {
    // 如果點擊當前已排序的欄位，則切換排序方向
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 否則設置新的排序欄位，並使用降序（大到小）作為默認
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 應用排序到數據
  const getSortedData = (data) => {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      // 對於名字排序，使用團體 → 期別 → kana 的順序
      if (sortField === 'name') {
        // 先比較團體
        const groupCompare = a.group.localeCompare(b.group);
        if (groupCompare !== 0) {
          return sortDirection === 'asc' ? groupCompare : -groupCompare;
        }

        // 團體相同，比較期別
        const generationA = parseInt(a.generation) || 0;
        const generationB = parseInt(b.generation) || 0;
        const generationCompare = generationA - generationB;
        if (generationCompare !== 0) {
          return sortDirection === 'asc' ? generationCompare : -generationCompare;
        }

        // 期別相同，比較 kana
        const kanaA = (a.kana || a.name).toLowerCase();
        const kanaB = (b.kana || b.name).toLowerCase();
        const kanaCompare = kanaA.localeCompare(kanaB, 'ja');
        return sortDirection === 'asc' ? kanaCompare : -kanaCompare;
      }

      // 對於團體字段進行特殊處理
      if (sortField === 'group') {
        const valueA = String(a[sortField]).toLowerCase();
        const valueB = String(b[sortField]).toLowerCase();

        if (sortDirection === 'asc') {
          return valueA.localeCompare(valueB);
        } else {
          return valueB.localeCompare(valueA);
        }
      }

      // 數字類型排序
      const valueA = a[sortField];
      const valueB = b[sortField];

      if (sortDirection === 'asc') {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  };

  // 渲染排序圖標
  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="sort-icon text-muted ms-1">↕</span>;
    }

    return (
      <span className="sort-icon ms-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // 渲染加載狀態
  const renderLoading = () => (
    <div className="text-center my-5">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3">載入統計數據中...</p>
    </div>
  );

  // 渲染錯誤訊息
  const renderError = () => (
    <Alert variant="danger" className="my-4">
      {error}
    </Alert>
  );

  // 渲染日期和團體選擇表單
  const renderDateForm = () => (
    <div className="mb-4 stats-form-container">
      <Form onSubmit={handleSubmit}>
        {activeTab === 'monthly' ? (
          <div>
            <Row className="align-items-end mb-3">
              <Col xs={12} md={5}>
                <Form.Group className="mb-3 mb-md-0">
                  <Form.Label>年份</Form.Label>
                  <Form.Select value={selectedYear} onChange={handleYearChange}>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}年</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={5}>
                <Form.Group className="mb-3 mb-md-0">
                  <Form.Label>月份</Form.Label>
                  <Form.Select value={selectedMonth} onChange={handleMonthChange}>
                    {monthOptions.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="d-flex justify-content-end">
                <Button type="submit" variant="primary" className="w-100">查詢</Button>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="mb-0">篩選團體和期生</Form.Label>
                  <Form.Check
                    type="switch"
                    id="advanced-mode-switch-monthly"
                    label="進階篩選（多選）"
                    checked={advancedMode}
                    onChange={handleModeToggle}
                  />
                </div>
              </Col>
            </Row>
            {!advancedMode ? (
              <Row>
                <Col xs={12} md={selectedGroup !== 'all' && getAvailableGenerations(statsData).length > 0 ? 6 : 12}>
                  <Form.Group>
                    <Form.Label>選擇團體</Form.Label>
                    <div>
                      <ButtonGroup className="w-100">
                        {groupOptions.map(group => (
                          <Button
                            key={group.value}
                            variant={selectedGroup === group.value ? 'primary' : 'outline-primary'}
                            onClick={() => handleGroupChange(group.value)}
                          >
                            {group.label}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </div>
                  </Form.Group>
                </Col>
                {selectedGroup !== 'all' && getAvailableGenerations(statsData).length > 0 && (
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>選擇期生</Form.Label>
                      <div>
                        <ButtonGroup className="w-100" style={{ flexWrap: 'wrap' }}>
                          <Button
                            variant={selectedGeneration === 'all' ? 'success' : 'outline-success'}
                            onClick={() => handleGenerationChange('all')}
                            style={{ flex: '0 0 auto', minWidth: '60px' }}
                          >
                            全部
                          </Button>
                          {getAvailableGenerations(statsData).map(gen => (
                            <Button
                              key={gen}
                              variant={selectedGeneration === gen ? 'success' : 'outline-success'}
                              onClick={() => handleGenerationChange(gen)}
                              style={{ flex: '0 0 auto', minWidth: '60px' }}
                            >
                              {gen}期
                            </Button>
                          ))}
                        </ButtonGroup>
                      </div>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            ) : (
              <>
                <Row className="mb-2">
                  <Col xs={12} className="text-end">
                    <Button size="sm" variant="outline-secondary" onClick={() => handleSelectAll(statsData)} className="me-2">
                      全選
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={handleClearAll}>
                      清除
                    </Button>
                  </Col>
                </Row>
                <Row>
                  {statsData && Object.entries(getAvailableFilters(statsData)).map(([group, generations]) => (
                    <Col xs={12} md={4} key={group} className="mb-3">
                      <div className="border rounded p-3">
                        <h6 className="mb-3">{getGroupDisplayName(group)}</h6>
                        <div>
                          {generations.map(gen => (
                            <Form.Check
                              key={`${group}-${gen}`}
                              type="checkbox"
                              id={`monthly-${group}-${gen}`}
                              label={`${gen}期`}
                              checked={isFilterSelected(group, gen)}
                              onChange={() => handleFilterToggle(group, gen)}
                              className="mb-2"
                            />
                          ))}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        ) : (
          <div>
            <Row className="align-items-end mb-3">
              <Col xs={12} md={10}>
                <Form.Group className="mb-3 mb-md-0">
                  <Form.Label>日期</Form.Label>
                  <Form.Control
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    max={format(new Date(), 'yyyy-MM-dd')}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={2} className="d-flex justify-content-end">
                <Button type="submit" variant="primary" className="w-100">查詢</Button>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="mb-0">篩選團體和期生</Form.Label>
                  <Form.Check
                    type="switch"
                    id="advanced-mode-switch-daily"
                    label="進階篩選（多選）"
                    checked={advancedMode}
                    onChange={handleModeToggle}
                  />
                </div>
              </Col>
            </Row>
            {!advancedMode ? (
              <Row>
                <Col xs={12} md={selectedGroup !== 'all' && getAvailableGenerations(statsData).length > 0 ? 6 : 12}>
                  <Form.Group>
                    <Form.Label>選擇團體</Form.Label>
                    <div>
                      <ButtonGroup className="w-100">
                        {groupOptions.map(group => (
                          <Button
                            key={group.value}
                            variant={selectedGroup === group.value ? 'primary' : 'outline-primary'}
                            onClick={() => handleGroupChange(group.value)}
                          >
                            {group.label}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </div>
                  </Form.Group>
                </Col>
                {selectedGroup !== 'all' && getAvailableGenerations(statsData).length > 0 && (
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>選擇期生</Form.Label>
                      <div>
                        <ButtonGroup className="w-100" style={{ flexWrap: 'wrap' }}>
                          <Button
                            variant={selectedGeneration === 'all' ? 'success' : 'outline-success'}
                            onClick={() => handleGenerationChange('all')}
                            style={{ flex: '0 0 auto', minWidth: '60px' }}
                          >
                            全部
                          </Button>
                          {getAvailableGenerations(statsData).map(gen => (
                            <Button
                              key={gen}
                              variant={selectedGeneration === gen ? 'success' : 'outline-success'}
                              onClick={() => handleGenerationChange(gen)}
                              style={{ flex: '0 0 auto', minWidth: '60px' }}
                            >
                              {gen}期
                            </Button>
                          ))}
                        </ButtonGroup>
                      </div>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            ) : (
              <>
                <Row className="mb-2">
                  <Col xs={12} className="text-end">
                    <Button size="sm" variant="outline-secondary" onClick={() => handleSelectAll(statsData)} className="me-2">
                      全選
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={handleClearAll}>
                      清除
                    </Button>
                  </Col>
                </Row>
                <Row>
                  {statsData && Object.entries(getAvailableFilters(statsData)).map(([group, generations]) => (
                    <Col xs={12} md={4} key={group} className="mb-3">
                      <div className="border rounded p-3">
                        <h6 className="mb-3">{getGroupDisplayName(group)}</h6>
                        <div>
                          {generations.map(gen => (
                            <Form.Check
                              key={`${group}-${gen}`}
                              type="checkbox"
                              id={`daily-${group}-${gen}`}
                              label={`${gen}期`}
                              checked={isFilterSelected(group, gen)}
                              onChange={() => handleFilterToggle(group, gen)}
                              className="mb-2"
                            />
                          ))}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        )}
      </Form>
    </div>
  );

  // 渲染統計摘要卡片
  const renderStatsSummary = (stats) => {
    if (!stats) return null;

    const memberCounts = getMemberCountByGroup(stats);
    const messageCounts = getMessageCountByGroup(stats);
    const totalMembers = Object.values(memberCounts).reduce((sum, count) => sum + count, 0);
    const totalMessages = Object.values(messageCounts).reduce((sum, count) => sum + count, 0);

    return (
      <div className="stats-summary mb-4">
        <Row>
          <Col xs={12} md={6} lg={4} className="mb-3">
            <div className="stats-summary-item">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">總成員數</h4>
                <div className="stats-number-inline">
                  <span className="stats-number-value">{totalMembers}</span>
                  <span className="stats-unit">人</span>
                </div>
              </div>
              <div className="group-breakdown">
                {Object.entries(memberCounts).map(([group, count]) => (
                  <div key={group} className="d-flex justify-content-between">
                    <span>{getGroupDisplayName(group)}</span>
                    <Badge bg="info">{count}人</Badge>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col xs={12} md={6} lg={4} className="mb-3">
            <div className="stats-summary-item">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">總訊息數</h4>
                <div className="stats-number-inline">
                  <span className="stats-number-value">{totalMessages}</span>
                  <span className="stats-unit">條</span>
                </div>
              </div>
              <div className="group-breakdown">
                {Object.entries(messageCounts).map(([group, count]) => (
                  <div key={group} className="d-flex justify-content-between">
                    <span>{getGroupDisplayName(group)}</span>
                    <Badge bg="primary">{count}條</Badge>
                  </div>
                ))}
              </div>
            </div>
          </Col>

          <Col xs={12} md={6} lg={4} className="mb-3">
            <div className="stats-summary-item">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">查詢時段</h4>
                <div>
                  <Badge bg="secondary" className="p-2">
                    {activeTab === 'monthly'
                      ? `${selectedYear}年${selectedMonth}月`
                      : format(parse(selectedDate, 'yyyy-MM-dd', new Date()), 'yyyy年MM月dd日')}
                  </Badge>
                </div>
              </div>
              <div className="group-breakdown">
                <div className="d-flex justify-content-between">
                  <span>平均訊息數</span>
                  <Badge bg="success">
                    {totalMembers > 0 ? Math.round(totalMessages / totalMembers * 10) / 10 : 0}條/人
                  </Badge>
                </div>
                {Object.entries(memberCounts).map(([group, count]) => (
                  count > 0 && (
                    <div key={`avg-${group}`} className="d-flex justify-content-between">
                      <span>{getGroupDisplayName(group)}平均</span>
                      <Badge bg="success">
                        {Math.round(messageCounts[group] / count * 10) / 10}條/人
                      </Badge>
                    </div>
                  )
                ))}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  // 渲染成員訊息統計表格
  const renderStatsTable = (stats) => {
    if (!stats) return null;

    const chartData = getSortedData(prepareChartData(stats));

    return (
      <div className="table-responsive">
        <Table striped bordered hover className="stats-table">
          <thead>
            <tr className="text-center">
              <th onClick={() => handleSort('rank')}>排名 {renderSortIcon('rank')}</th>
              <th onClick={() => handleSort('name')}>成員 {renderSortIcon('name')}</th>
              <th onClick={() => handleSort('group')}>所屬組合 {renderSortIcon('group')}</th>
              <th onClick={() => handleSort('total')}>總訊息數 {renderSortIcon('total')}</th>
              <th onClick={() => handleSort('text')}>文字 {renderSortIcon('text')}</th>
              <th onClick={() => handleSort('image')}>圖片 {renderSortIcon('image')}</th>
              <th onClick={() => handleSort('audio')}>語音 {renderSortIcon('audio')}</th>
              <th onClick={() => handleSort('video')}>影片 {renderSortIcon('video')}</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((item) => (
              <tr key={item.name} className="text-center">
                <td>{item.rank}</td>
                <td>{item.name}</td>
                <td>{getGroupDisplayName(item.group)}</td>
                <td><strong>{item.total}</strong></td>
                <td>{item.text}</td>
                <td>{item.image}</td>
                <td>{item.audio}</td>
                <td>{item.video}</td>
              </tr>
            ))}
            {chartData.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4">沒有找到任何訊息統計數據</td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  // 渲染圖表
  const renderChart = (stats) => {
    if (!stats) return null;

    const chartData = prepareChartData(stats);

    // 如果數據太多，只顯示前20名
    const topData = chartData.slice(0, 20);

    return (
      <div className="stats-chart-container mb-4">
        <h5 className="text-center mb-4">
          成員訊息統計
        </h5>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={topData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              tick={{ angle: -45, textAnchor: 'end' }}
              height={60}
            />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="text" name="文字" stackId="a" fill="#8884d8" />
            <Bar dataKey="image" name="圖片" stackId="a" fill="#82ca9d" />
            <Bar dataKey="audio" name="語音" stackId="a" fill="#ffc658" />
            <Bar dataKey="video" name="影片" stackId="a" fill="#ff8042" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="stats-page-container">
      <Container fluid className="py-5">
        <div className="content-wrapper">
          <h1 className="text-center mb-4">訊息統計分析</h1>

          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setActiveTab(key)}
            className="mb-4 stats-tabs"
          >
            <Tab eventKey="monthly" title="月度統計">
              {renderDateForm()}

              {loading ? (
                renderLoading()
              ) : error ? (
                renderError()
              ) : statsData ? (
                <>
                  {renderStatsSummary(statsData)}
                  {renderChart(statsData)}
                  {renderStatsTable(statsData)}
                </>
              ) : (
                <Alert variant="info" className="text-center">
                  請選擇日期範圍並點擊查詢按鈕
                </Alert>
              )}
            </Tab>

            <Tab eventKey="daily" title="每日統計">
              {renderDateForm()}

              {loading ? (
                renderLoading()
              ) : error ? (
                renderError()
              ) : statsData ? (
                <>
                  {renderStatsSummary(statsData)}
                  {renderChart(statsData)}
                  {renderStatsTable(statsData)}
                </>
              ) : (
                <Alert variant="info" className="text-center">
                  請選擇日期並點擊查詢按鈕
                </Alert>
              )}
            </Tab>
          </Tabs>
        </div>
      </Container>
    </div>
  );
};

export default StatsPage;